import { useEffect, useState } from 'react'

import { Button } from '@clientbase/clientbase-library'
import { Dialog, DialogContent, IconButton, Stack } from '@mui/material'

import { theme } from 'styles/theme'

import * as S from './PwaInstallPrompt.styles'

const DISMISS_KEY = 'pwa-install-dismissed'
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const isStandaloneMode = (): boolean => {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  const nav = window.navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true
}

const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent)
}

const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false
  return /Android/i.test(window.navigator.userAgent)
}

const isIosNonSafari = (): boolean => {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  const iOS = /iPad|iPhone|iPod/.test(ua)
  if (!iOS) return false
  // Chrome iOS: CriOS | Firefox iOS: FxiOS | Edge iOS: EdgiOS | Opera iOS: OPiOS
  return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
}

const MOBILE_BREAKPOINT = 768

const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.innerWidth < MOBILE_BREAKPOINT
}

const isDismissed = (): boolean => {
  if (typeof window === 'undefined') return true
  const ts = localStorage.getItem(DISMISS_KEY)
  if (!ts) return false
  const dismissedAt = parseInt(ts, 10)
  if (Number.isNaN(dismissedAt)) return false
  return Date.now() - dismissedAt < DISMISS_TTL_MS
}

// ============ SVG Icons ============

const HomeIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9.5 12 3l9 6.5V20a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5Z" />
    <path d="M9 22V12h6v10" />
  </svg>
)

const PhoneIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <path d="M12 18h.01" />
  </svg>
)

const ShareIosIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3v12" />
    <path d="m8 7 4-4 4 4" />
    <path d="M20 14v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5" />
  </svg>
)

const CloseIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [standalone, setStandalone] = useState(false)
  const [isIosDialogOpen, setIsIosDialogOpen] = useState(false)
  const [ios, setIos] = useState(false)
  const [android, setAndroid] = useState(false)
  const [iosNonSafari, setIosNonSafari] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    setIos(isIOS())
    setAndroid(isAndroid())
    setIosNonSafari(isIosNonSafari())
    setStandalone(isStandaloneMode())
    setDismissed(isDismissed())

    const updateMobile = () => setIsMobile(isMobileDevice())
    updateMobile()
    window.addEventListener('resize', updateMobile)

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    return () => {
      window.removeEventListener('resize', updateMobile)
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      )
    }
  }, [])

  const isVisible = isMobile && !standalone && !dismissed

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString())
    setDismissed(true)
  }

  const handleInstallClick = async () => {
    if (ios || !deferredPrompt) {
      setIsIosDialogOpen(true)
      return
    }
    try {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      if (choice.outcome === 'accepted') {
        setDismissed(true)
      }
    } catch (error) {
      console.error('[pwa] install prompt failed:', error)
    } finally {
      setDeferredPrompt(null)
    }
  }

  if (!isVisible) return null

  return (
    <>
      <S.Banner>
        <S.Content>
          <S.IconBox>
            <span style={{ color: '#ffffff', display: 'flex' }}>
              <HomeIcon size={22} />
            </span>
          </S.IconBox>
          <S.Texts>
            <S.Title>Instale a Base de Clientes no seu celular</S.Title>
            <S.Body>
              Acesso rápido pela tela inicial e receba notificações mesmo com o
              app fechado.
            </S.Body>
          </S.Texts>
        </S.Content>
        <S.Actions>
          <S.DismissButton onClick={handleDismiss} type="button">
            Agora não
          </S.DismissButton>
          <Button variantButton="primary" onClick={handleInstallClick}>
            {ios ? 'Como instalar' : 'Instalar'}
          </Button>
        </S.Actions>
      </S.Banner>

      {/* iOS install instructions — flat style inspired by AdsHealth */}
      <Dialog
        open={isIosDialogOpen}
        onClose={() => setIsIosDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ padding: '24px' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={2.5}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <span
                style={{
                  color: theme.palette.neutral[300],
                  display: 'flex'
                }}
              >
                <PhoneIcon size={20} />
              </span>
              <span
                style={{
                  fontSize: '17px',
                  fontWeight: 700,
                  color: theme.palette.neutral[200]
                }}
              >
                {android
                  ? 'Instalar no Android'
                  : iosNonSafari
                  ? 'Instalar no Chrome (iPhone)'
                  : 'Instalar no iPhone'}
              </span>
            </Stack>
            <IconButton
              onClick={() => setIsIosDialogOpen(false)}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: theme.palette.neutral[400],
                '&:hover': { backgroundColor: theme.palette.neutral[800] }
              }}
            >
              <CloseIcon size={18} />
            </IconButton>
          </Stack>

          {iosNonSafari ? (
            <>
              <S.StepRow>
                <S.StepNumber>1</S.StepNumber>
                <S.StepText>
                  Toque no botão <strong>Compartilhar</strong> na barra superior
                  do Chrome.
                </S.StepText>
                <S.StepIcon>
                  <ShareIosIcon size={22} />
                </S.StepIcon>
              </S.StepRow>

              <S.StepRow>
                <S.StepNumber>2</S.StepNumber>
                <S.StepText>
                  Role a lista e toque em{' '}
                  <strong>&quot;Adicionar à Tela de Início&quot;</strong>.
                </S.StepText>
              </S.StepRow>

              <S.StepRow>
                <S.StepNumber>3</S.StepNumber>
                <S.StepText>
                  Confirme tocando em <strong>&quot;Adicionar&quot;</strong>.
                </S.StepText>
              </S.StepRow>
            </>
          ) : android ? (
            <>
              <S.StepRow>
                <S.StepNumber>1</S.StepNumber>
                <S.StepText>
                  Toque no ícone de <strong>menu</strong> (⋮) no canto superior
                  direito do Chrome.
                </S.StepText>
              </S.StepRow>

              <S.StepRow>
                <S.StepNumber>2</S.StepNumber>
                <S.StepText>
                  Toque em <strong>&quot;Adicionar à tela inicial&quot;</strong>{' '}
                  ou <strong>&quot;Instalar app&quot;</strong>.
                </S.StepText>
              </S.StepRow>

              <S.StepRow>
                <S.StepNumber>3</S.StepNumber>
                <S.StepText>
                  Confirme tocando em <strong>&quot;Instalar&quot;</strong>.
                </S.StepText>
              </S.StepRow>
            </>
          ) : (
            <>
              <S.StepRow>
                <S.StepNumber>1</S.StepNumber>
                <S.StepText>
                  Toque no botão <strong>Compartilhar</strong> na barra inferior
                  do Safari.
                </S.StepText>
                <S.StepIcon>
                  <ShareIosIcon size={22} />
                </S.StepIcon>
              </S.StepRow>

              <S.StepRow>
                <S.StepNumber>2</S.StepNumber>
                <S.StepText>
                  Role a lista e toque em{' '}
                  <strong>&quot;Adicionar à Tela de Início&quot;</strong>.
                </S.StepText>
              </S.StepRow>

              <S.StepRow>
                <S.StepNumber>3</S.StepNumber>
                <S.StepText>
                  Confirme tocando em <strong>&quot;Adicionar&quot;</strong> no
                  canto superior direito.
                </S.StepText>
              </S.StepRow>
            </>
          )}

          <Button
            variantButton="primary"
            fullWidth
            onClick={() => setIsIosDialogOpen(false)}
            sx={{ marginTop: '20px', height: '48px', fontSize: '15px' }}
          >
            Entendi
          </Button>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PwaInstallPrompt
